import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { createRequest } from "src/api/inventory";
import { SelectField } from "src/components/form";
import ProductItemsField, { stockItemValidator, type StockItemsFormValues } from "src/components/inventory/ProductItemsField";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useDashboardContext } from "src/context/DashboardContext";
import { showSuccessToast } from "src/lib/utils";

interface RequestFormValues extends StockItemsFormValues {
	from_id: string;
	to_id: string;
}

const validationSchema = Yup.object().shape({
	from_id: Yup.string().required("From entity is required"),
	to_id: Yup.string().required("To entity is required"),
	items: Yup.array().of(stockItemValidator).min(1, "At least one item must be provided"),
});

const initialValues: RequestFormValues = { from_id: "", to_id: "", items: [] };

/** Stock transfer request between two stores/warehouses. */
const RequestProducts = () => {
	const { entities } = useDashboardContext();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const options = entities.map(({ id, name }) => ({ label: name, value: String(id) }));

	const { mutate, isLoading } = useMutation(createRequest, {
		onSuccess: () => {
			showSuccessToast("Request created successfully");
			queryClient.invalidateQueries(["requests"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<SelectField
							label="From store / warehouse"
							name="from_id"
							placeholder="Select store or warehouse"
							value={values.from_id}
							error={touched.from_id && errors.from_id}
							onChange={(value) => setFieldValue("from_id", value)}
							options={options}
							emptyMessage="No stores or warehouses yet"
							required
						/>
						<SelectField
							label="To store / warehouse"
							name="to_id"
							placeholder="Select store or warehouse"
							value={values.to_id}
							error={touched.to_id && errors.to_id}
							onChange={(value) => setFieldValue("to_id", value)}
							options={options}
							emptyMessage="No stores or warehouses yet"
							required
						/>
					</div>

					<ProductItemsField showSku emptyDescription="Search for a product above to add it to this request." />

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Sending..." : "Make request"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default RequestProducts;
